import { useEffect, useRef } from "react";
import "@/App.css";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "@phosphor-icons/react";
import { BrowserRouter, Routes, Route, Outlet, Navigate, useNavigate, useLocation } from "react-router-dom";
import { SelectionProvider, useSelection } from "@/components/site/SelectionContext";
import { CartProvider } from "@/cart";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Contact";
import Home from "@/pages/Home";
import CollectionsPage from "@/pages/Collections";
import ProductsPage from "@/pages/Products";
import BespokePage from "@/pages/Bespoke";
import AboutPage from "@/pages/About";
import ContactPage from "@/pages/Contact";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import CartPage from "@/pages/CartPage";
import Checkout from "@/pages/Checkout";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";

const SelectionBar = () => {
  const { items, clear } = useSelection();
  const navigate = useNavigate();
  return (
    <AnimatePresence>
      {items.length > 0 && (
        <motion.div data-testid="selection-bar" initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.76, 0, 0.24, 1] }}
          className="fixed bottom-5 inset-x-4 md:inset-x-auto md:right-8 z-40 flex items-center gap-4 bg-[var(--text-primary)] text-[var(--bg-primary)] px-5 py-4 shadow-2xl">
          <span className="font-mono text-[11px] tracking-[0.18em] uppercase">{items.length} enquiry item{items.length > 1 ? "s" : ""}</span>
          <button onClick={() => navigate("/contact")} className="inline-flex items-center gap-2 bg-[var(--gold)] text-[#2B2620] px-4 h-10 font-mono text-[10px] tracking-[0.18em] uppercase hover:opacity-90">Request quote <ArrowRight size={14} /></button>
          <button onClick={clear} className="font-mono text-[10px] tracking-[0.18em] uppercase opacity-60 hover:opacity-100">Clear</button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Layout = () => {
  const lenisRef = useRef(null);
  const { pathname } = useLocation();
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.2, easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)) });
    lenisRef.current = lenis;
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);
  useEffect(() => { lenisRef.current?.scrollTo(0, { immediate: true }); window.scrollTo(0, 0); }, [pathname]);

  return (
    <div className="App grain">
      <Nav />
      <Outlet />
      <Footer />
      <SelectionBar />
    </div>
  );
};

function App() {
  return (
    <SelectionProvider>
      <CartProvider>
        <BrowserRouter>
          <Toaster position="bottom-right" theme="light" toastOptions={{ style: { background: "#F2EBDD", border: "1px solid #D8CBB4", color: "#2B2620", borderRadius: 0, fontFamily: "Outfit, sans-serif" } }} />
          <Routes>
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/collections" element={<CollectionsPage />} />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/bespoke" element={<BespokePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/product/:id" element={<ProductDetail />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
            </Route>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </CartProvider>
    </SelectionProvider>
  );
}

export default App;
